/**
 * Funciones de BD para el sistema de plantillas de curso.
 *
 * Lectura: cualquier autenticado puede leer el catálogo.
 * Escritura: solo admins (lo enforce la RLS).
 */
import { supabase } from './supabase';
import type {
  CourseTemplate, TemplateTopic, TemplateSubtopic, TemplateDelivery, TemplateBundle, CalendarRow,
} from '../types/templates';

// ─── Lectura ───────────────────────────────────────────────────────────

/** Lista todas las plantillas activas, ordenadas por nombre. */
export async function listTemplates(): Promise<CourseTemplate[]> {
  const { data, error } = await supabase
    .from('course_templates')
    .select('*')
    .eq('status', 'active')
    .order('name', { ascending: true });
  if (error) {
    console.error('[templates] listTemplates error:', error);
    return [];
  }
  return (data ?? []) as CourseTemplate[];
}

/**
 * Trae una plantilla con sus temas y entregas en una sola llamada.
 * Pensado para alimentar el modal de "Crear curso desde plantilla".
 */
export async function getTemplateBundle(templateId: string): Promise<TemplateBundle | null> {
  // 3 queries paralelas — más rápido que un join anidado y soporta RLS por tabla
  const [tplRes, topicsRes, delivRes] = await Promise.all([
    supabase.from('course_templates').select('*').eq('id', templateId).maybeSingle(),
    supabase.from('template_topics')
      .select('*')
      .eq('template_id', templateId)
      .order('default_class_number', { ascending: true }),
    supabase.from('template_deliveries')
      .select('*')
      .eq('template_id', templateId)
      .order('delivery_number', { ascending: true }),
  ]);
  if (tplRes.error || !tplRes.data) {
    console.error('[templates] getTemplateBundle: plantilla no encontrada', tplRes.error);
    return null;
  }
  return {
    template:   tplRes.data as CourseTemplate,
    topics:     (topicsRes.data ?? []) as TemplateTopic[],
    deliveries: (delivRes.data ?? []) as TemplateDelivery[],
  };
}

/**
 * Trae los subtemas agrupados por topic_id. Útil para armar la descripción
 * del módulo cuando se crea un curso desde plantilla.
 */
export async function listSubtopicsByTopic(topicIds: string[]): Promise<Record<string, TemplateSubtopic[]>> {
  if (topicIds.length === 0) return {};
  const { data, error } = await supabase
    .from('template_subtopics')
    .select('*')
    .in('topic_id', topicIds)
    .order('order_in_topic', { ascending: true });
  if (error) {
    console.error('[templates] listSubtopicsByTopic error:', error);
    return {};
  }
  const map: Record<string, TemplateSubtopic[]> = {};
  for (const s of (data ?? []) as TemplateSubtopic[]) {
    (map[s.topic_id] ??= []).push(s);
  }
  return map;
}

// ─── Creación de un curso a partir de una plantilla ───────────────────

/** Datos que el modal junta antes de invocar createCourseFromTemplate. */
export interface CreateCourseInput {
  template_id: string;
  /** Datos del curso */
  name: string;
  description?: string | null;
  docente_id: string;
  secondary_docente_id?: string | null;
  start_date?: string | null;       // ISO date — opcional, deducible de calendar[0]
  end_date?: string | null;         // ISO date — opcional, deducible del último
  status?: 'active' | 'draft' | 'completed';
  /** Calendario parseado del Excel */
  calendar: CalendarRow[];
  /** Horario por defecto (HH:MM) aplicado a todas las clases */
  default_start_time: string;       // "19:00"
  default_end_time:   string;       // "22:00"
  /** Link Zoom común (opcional) */
  default_zoom_link?: string | null;
}

/**
 * Crea un curso completo a partir de una plantilla:
 *   1) INSERT en courses (con template_id)
 *   2) Para cada CLASE del calendario: INSERT en modules con
 *      - topic_id  → resolvió al topic con default_class_number coincidente
 *      - name      → copiado del topic (para que la UI vieja siga igual)
 *      - description / objective → idem
 *      - start_date / end_date → fecha del Excel + horarios default
 *   3) Para cada ENTREGA del calendario: INSERT en course_deliveries
 *
 * Retorna el course_id creado, o null si algo falla (ya loggeó por consola).
 */
export async function createCourseFromTemplate(input: CreateCourseInput): Promise<string | null> {
  try {
    // 1) Traer topics y deliveries de la plantilla para mapear los nombres
    const bundle = await getTemplateBundle(input.template_id);
    if (!bundle) {
      console.error('[templates] createCourseFromTemplate: plantilla no existe');
      return null;
    }
    const topicByClass = new Map<number, TemplateTopic>();
    bundle.topics.forEach((t) => topicByClass.set(t.default_class_number, t));
    const deliveryByNumber = new Map<number, TemplateDelivery>();
    bundle.deliveries.forEach((d) => deliveryByNumber.set(d.delivery_number, d));

    // 1b) Traer subtemas de cada topic para armar la descripción del módulo
    //     (replica el formato del curso viejo: "1. ... 2. ... 3. ...").
    //     Si el topic no tiene subtemas, cae al description del topic.
    const subtopicsByTopic = await listSubtopicsByTopic(bundle.topics.map(t => t.id));

    // 2) Calcular fechas mínima/máxima si no vinieron explícitas
    const classDates = input.calendar.filter(r => r.class_number !== null).map(r => r.date).sort();
    const startDate = input.start_date ?? classDates[0]                     ?? null;
    const endDate   = input.end_date   ?? classDates[classDates.length - 1] ?? null;

    // 3) Crear el curso
    const courseInsert: Record<string, unknown> = {
      name:        input.name,
      description: input.description ?? null,
      docente_id:  input.docente_id,
      secondary_docente_id: input.secondary_docente_id ?? null,
      start_date:  startDate,
      end_date:    endDate,
      status:      input.status ?? 'active',
      template_id: input.template_id,
    };
    const { data: course, error: courseErr } = await supabase
      .from('courses')
      .insert(courseInsert)
      .select('id')
      .single();
    if (courseErr || !course) {
      console.error('[templates] insertando curso:', courseErr);
      return null;
    }
    const courseId = course.id as string;

    // 4) Insertar los modules (uno por CLASE del calendario)
    //    Usamos la fecha + horario para start_date/end_date (timestamptz).
    const moduleRows = input.calendar
      .filter(r => r.class_number !== null)
      .map((r, idx) => {
        const topic = topicByClass.get(r.class_number!);
        const startISO = buildTimestamp(r.date, input.default_start_time);
        const endISO   = buildTimestamp(r.date, input.default_end_time);

        // ── Formato del NOMBRE ──
        // Replica el formato del curso viejo: "{N}.- TÍTULO EN MAYÚSCULAS"
        // Ej: "1.- INTRODUCCIÓN Y PRESENTACIÓN"
        const topicNameUpper = (topic?.name ?? r.display_label ?? `Clase ${r.class_number}`).toUpperCase();
        const moduleName = `${r.class_number}.- ${topicNameUpper}`;

        // ── Formato de la DESCRIPCIÓN ──
        // Concatenar los subtemas en un párrafo continuo (con espacios),
        // igual que el curso viejo: "1. Bienvenida... 2. Presentación..."
        const subs = topic ? (subtopicsByTopic[topic.id] ?? []) : [];
        const description =
          subs.length > 0
            ? subs.map(s => s.title.trim()).filter(Boolean).join(' ')
            : (topic?.description ?? null);

        return {
          course_id:    courseId,
          name:         moduleName,
          description,
          objective:    topic?.objective ?? null,
          docente_id:   input.docente_id,
          order_index:  idx + 1,
          start_date:   startISO,
          end_date:     endISO,
          zoom_link:    input.default_zoom_link ?? null,
          status:       'pending',
          // Metadata nueva (invisible para UI vieja)
          topic_id:     topic?.id ?? null,
          class_number: r.class_number,
        };
      });
    if (moduleRows.length > 0) {
      const { error: modErr } = await supabase.from('modules').insert(moduleRows);
      if (modErr) {
        console.error('[templates] insertando modules:', modErr);
        // Curso quedó creado pero sin módulos — informar al caller
        return courseId;
      }
    }

    // 5) Insertar las entregas (course_deliveries)
    const deliveryRows = input.calendar
      .filter(r => r.delivery_number !== null)
      .map((r) => {
        const tplDelivery = deliveryByNumber.get(r.delivery_number!);
        return {
          course_id:            courseId,
          template_delivery_id: tplDelivery?.id ?? null,
          delivery_number:      r.delivery_number,
          name:                 tplDelivery?.name ?? r.display_label ?? `Entrega ${r.delivery_number}`,
          description:          tplDelivery?.description ?? null,
          delivery_date:        r.date,
          status:               'pending',
        };
      });
    if (deliveryRows.length > 0) {
      const { error: delErr } = await supabase.from('course_deliveries').insert(deliveryRows);
      if (delErr) {
        console.error('[templates] insertando deliveries:', delErr);
      }
    }

    return courseId;
  } catch (e) {
    console.error('[templates] createCourseFromTemplate: excepción inesperada', e);
    return null;
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────

/**
 * Combina una fecha (YYYY-MM-DD) con un horario (HH:MM) y devuelve ISO timestamp.
 * Importante: armamos el string sin Z para que Supabase lo guarde como "hora local"
 * tal como el resto del schema (los <input type="datetime-local"> también funcionan
 * así). El formato final es "YYYY-MM-DDTHH:mm:00".
 */
function buildTimestamp(dateYmd: string, timeHm: string): string {
  // Aceptar tanto "YYYY-MM-DD" como "YYYY-MM-DDTHH:mm:ss..."
  const datePart = dateYmd.includes('T') ? dateYmd.split('T')[0] : dateYmd;
  const [hh, mm] = timeHm.split(':');
  const hour = (hh || '00').padStart(2, '0');
  const min  = (mm || '00').padStart(2, '0');
  return `${datePart}T${hour}:${min}:00`;
}
